// Aba "Acessos" do Catalogo Digital — Fase 0 Estrategia Google.
// Mostra, DENTRO do sistema, se o catalogo esta recebendo gente: aberturas da
// vitrine, visualizacoes de produto, cliques no WhatsApp, de onde vieram (Google,
// WhatsApp, etc), evolucao por dia e os produtos mais vistos. Robos sao filtrados
// no servidor — aqui e so gente. Sao VISUALIZACOES, nao visitantes unicos (sem
// cookie/login publico nao da pra dizer "pessoas diferentes").
import { useEffect, useState } from 'react';
import { Eye, Store, MessageCircle, TrendingUp } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';

interface Metricas {
  dias: number;
  resumo: {
    visualizacoes: number;
    aberturas_vitrine: number;
    views_produto: number;
    cliques_whatsapp: number;
  };
  por_dia: { dia: string; visualizacoes: number; whatsapp: number }[];
  por_origem: { origem: string; total: number }[];
  top_produtos: { produto_id: number; descricao: string; views: number }[];
}

// Rotulo amigavel + cor de cada origem (cor + texto — seguro pra daltonismo).
const ORIGEM: Record<string, { rotulo: string; cor: string }> = {
  google: { rotulo: 'Busca do Google', cor: '#16a34a' },
  whatsapp: { rotulo: 'Link do WhatsApp', cor: '#25d366' },
  social: { rotulo: 'Instagram / Facebook', cor: '#8b5cf6' },
  direto: { rotulo: 'Acesso direto', cor: '#6b7280' },
  interno: { rotulo: 'Navegacao no site', cor: '#9ca3af' },
  outro: { rotulo: 'Outros sites', cor: '#d97706' },
};

const PERIODOS = [
  { dias: 7, rotulo: '7 dias' },
  { dias: 30, rotulo: '30 dias' },
  { dias: 90, rotulo: '90 dias' },
];

function fmtData(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

export function CatalogoMetricas() {
  const toast = useToast();
  const [dias, setDias] = useState(30);
  const [dados, setDados] = useState<Metricas | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    api
      .get<Metricas>('/api/catalogo-metricas', { query: { dias } })
      .then((r) => vivo && setDados(r))
      .catch((e: any) => vivo && toast.erro(e.message ?? 'Falha ao carregar as metricas'))
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dias]);

  const r = dados?.resumo;
  const conversao = r && r.visualizacoes > 0 ? (r.cliques_whatsapp / r.visualizacoes) * 100 : 0;
  const maxDia = Math.max(1, ...(dados?.por_dia ?? []).map((d) => d.visualizacoes));
  const totalOrigem = Math.max(1, (dados?.por_origem ?? []).reduce((s, o) => s + o.total, 0));

  const cards = [
    { icone: <Eye size={18} />, valor: r?.visualizacoes ?? 0, rotulo: 'Visualizacoes', dica: 'Aberturas da vitrine + visualizacoes de produto (so gente, robos fora)' },
    { icone: <Store size={18} />, valor: r?.aberturas_vitrine ?? 0, rotulo: 'Aberturas da vitrine', dica: 'Quantas vezes abriram a pagina /catalogo' },
    { icone: <TrendingUp size={18} />, valor: r?.views_produto ?? 0, rotulo: 'Views de produto', dica: 'Visualizacoes de paginas de produto' },
    { icone: <MessageCircle size={18} />, valor: r?.cliques_whatsapp ?? 0, rotulo: 'Cliques no WhatsApp', dica: 'Cliques no botao "Falar pelo WhatsApp" — o sinal de venda' },
  ];

  return (
    <div>
      {/* Seletor de periodo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted, #6b7280)' }}>Periodo:</span>
        {PERIODOS.map((p) => (
          <button
            key={p.dias}
            type="button"
            onClick={() => setDias(p.dias)}
            className={dias === p.dias ? 'btn-primario' : 'btn-secundario'}
            style={{ padding: '4px 14px', fontSize: 13 }}
          >
            {p.rotulo}
          </button>
        ))}
      </div>

      {carregando ? (
        <p className="muted">Carregando...</p>
      ) : !dados || dados.resumo.visualizacoes + dados.resumo.cliques_whatsapp === 0 ? (
        <div className="muted" style={{ padding: 24, lineHeight: 1.7 }}>
          Ainda sem acessos registrados neste periodo.
          <br />
          A contagem comeca a partir de agora — conforme as pessoas abrirem o catalogo, os numeros aparecem aqui.
        </div>
      ) : (
        <>
          {/* Numeros-chave */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 22 }}>
            {cards.map((c) => (
              <div
                key={c.rotulo}
                title={c.dica}
                style={{
                  border: '1px solid var(--panel-border, #e5e7eb)',
                  borderRadius: 8,
                  padding: '14px 16px',
                  background: 'var(--panel-bg, #fff)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted, #6b7280)', fontSize: 12.5, fontWeight: 600 }}>
                  {c.icone} {c.rotulo}
                </div>
                <div style={{ fontSize: 30, fontWeight: 800, marginTop: 6, lineHeight: 1 }}>{c.valor.toLocaleString('pt-BR')}</div>
              </div>
            ))}
          </div>

          {/* Conversao (cliques / visualizacoes) */}
          <div
            style={{
              backgroundColor: '#fde68a',
              color: '#000',
              borderRadius: 8,
              padding: '10px 16px',
              marginBottom: 24,
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {conversao.toFixed(1)}% das visualizacoes viraram clique no WhatsApp
            <span style={{ fontWeight: 400 }}> — esse e o sinal mais perto de venda.</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 28 }}>
            {/* Por dia */}
            <div>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Visualizacoes por dia</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {dados.por_dia.map((d) => (
                  <div key={d.dia} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    <span style={{ width: 44, color: 'var(--text-muted, #6b7280)', flexShrink: 0 }}>{fmtData(d.dia)}</span>
                    <div style={{ flex: 1, background: 'var(--grid-header-bg, #f3f4f6)', borderRadius: 4, height: 18, position: 'relative', overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${(d.visualizacoes / maxDia) * 100}%`,
                          height: '100%',
                          background: 'var(--primary, #b45309)',
                          borderRadius: 4,
                          minWidth: d.visualizacoes > 0 ? 3 : 0,
                        }}
                      />
                    </div>
                    <span style={{ width: 60, textAlign: 'right', fontWeight: 600, flexShrink: 0 }}>
                      {d.visualizacoes}
                      {d.whatsapp > 0 && <span style={{ color: '#16a34a', fontWeight: 700 }} title="cliques no WhatsApp neste dia"> · {d.whatsapp}<MessageCircle size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /></span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
              {/* Por origem */}
              <div>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>De onde vieram</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {dados.por_origem.map((o) => {
                    const info = ORIGEM[o.origem] ?? { rotulo: o.origem, cor: '#6b7280' };
                    const pct = (o.total / totalOrigem) * 100;
                    return (
                      <div key={o.origem} style={{ fontSize: 12.5 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                            <span style={{ width: 10, height: 10, borderRadius: '50%', background: info.cor, flexShrink: 0 }} />
                            {info.rotulo}
                          </span>
                          <span style={{ color: 'var(--text-muted, #6b7280)' }}>{o.total} ({pct.toFixed(0)}%)</span>
                        </div>
                        <div style={{ background: 'var(--grid-header-bg, #f3f4f6)', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: info.cor, borderRadius: 4 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Top produtos */}
              {dados.top_produtos.length > 0 && (
                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Produtos mais vistos</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {dados.top_produtos.map((p, i) => (
                      <div key={p.produto_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                        <span style={{ width: 18, color: 'var(--text-muted, #9ca3af)', fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.descricao}</span>
                        <span style={{ fontWeight: 700, flexShrink: 0 }}>{p.views}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
